import { Check, PenTool, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const ImageEditor = ({ imageFile, onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [color, setColor] = useState("#ef4444"); // Red default
  const [isDrawing, setIsDrawing] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Load image onto canvas
  useEffect(() => {
    if (!imageFile || !canvasRef.current || !containerRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    
    img.onload = () => {
      // Fit to container width, maintain aspect ratio
      const containerWidth = containerRef.current.clientWidth;
      const scale = containerWidth / img.width;
      canvas.width = containerWidth;
      canvas.height = img.height * scale;
      
      // Draw initial image
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      setImageLoaded(true);
    };

    img.src = URL.createObjectURL(imageFile);
    
    return () => URL.revokeObjectURL(img.src);
  }, [imageFile]);

  const startDrawing = (e) => {
    if (!imageLoaded) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || !imageLoaded) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const handleSave = () => {
    if (!canvasRef.current) return;
    canvasRef.current.toBlob((blob) => {
      onSave(blob);
    }, "image/jpeg", 0.9);
  };

  const handleClear = () => {
    // Reload image to clear drawing
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = URL.createObjectURL(imageFile);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl animate-scale-in">
        
        {/* Header */}
        <div className="p-4 border-b border-slate-700 flex justify-between items-center">
          <h3 className="text-white font-medium flex items-center gap-2">
            <PenTool size={18} className="text-indigo-400" />
            Annotate Chart
          </h3>
          <div className="flex gap-2">
            <button 
              onClick={() => setColor("#ef4444")} // Red
              className={`w-6 h-6 rounded-full border-2 ${color === "#ef4444" ? "border-white scale-110" : "border-transparent" } bg-red-500 transition-transform`}
            />
            <button 
              onClick={() => setColor("#22c55e")} // Green
              className={`w-6 h-6 rounded-full border-2 ${color === "#22c55e" ? "border-white scale-110" : "border-transparent" } bg-green-500 transition-transform`}
            />
            <button 
              onClick={() => setColor("#eab308")} // Yellow
              className={`w-6 h-6 rounded-full border-2 ${color === "#eab308" ? "border-white scale-110" : "border-transparent" } bg-yellow-500 transition-transform`}
            />
            <div className="w-px h-6 bg-slate-600 mx-2"></div>
            <button 
              onClick={handleClear}
              className="text-slate-400 hover:text-white transition-colors"
              title="Clear Drawing"
            >
              <RefreshCw size={20} />
            </button>
          </div>
        </div>

        {/* Canvas Area */}
        <div 
          ref={containerRef}
          className="flex-1 overflow-hidden bg-slate-900 flex items-center justify-center p-4 relative"
        >
          <canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            className="cursor-crosshair max-w-full shadow-lg"
          />
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 flex justify-end gap-3 bg-slate-800 rounded-b-xl">
          <button 
            onClick={onCancel}
            className="px-4 py-2 text-slate-300 hover:text-white transition-colors font-medium text-sm"
          >
            Cancel
          </button>
          <button 
            onClick={handleSave}
            className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2 rounded-lg font-medium transition-all shadow-lg flex items-center gap-2 text-sm"
          >
            <Check size={16} />
            Save Annotation
          </button>
        </div>

      </div>
    </div>
  );
};

export default ImageEditor;
